import React from 'react';
// Rimosso useState, ChevronDown, ChevronRight
import type { LayerBase, LayerStyleProperties } from '../../../../store/types';
import { StylePropertyInput } from '../../../../common/StylePropertyInput';
import type { StyleValue } from '../../../../common/StylePropertyInput';
import { HierarchicalAccordion } from '../../../../common/HierarchicalAccordion';
import { HierarchicalAccordionItem } from '../../../../common/HierarchicalAccordionItem';

interface StyleEditorSectionProps {
  layer: LayerBase & LayerStyleProperties;
  updateLayerStyle: (
    layerId: string,
    propertyName: keyof LayerStyleProperties,
    value: StyleValue
  ) => void;
  isDisabled: boolean;
}

export const LineLayoutProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  if (layer.maplibreLayerType !== 'line') return null;
  return (
    <>
      {/* Line Cap */}
      <StylePropertyInput
        key={`${layer.id}-line-cap`}
        layerId={layer.id}
        propertyName="lineCap"
        label="Line Cap"
        currentValue={layer.lineCap}
        propertyType="select"
        selectOptions={[
          { value: 'butt', label: 'Butt' },
          { value: 'round', label: 'Round' },
          { value: 'square', label: 'Square' },
        ]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* Line Join */}
      <StylePropertyInput
        key={`${layer.id}-line-join`}
        layerId={layer.id}
        propertyName="lineJoin"
        label="Line Join"
        currentValue={layer.lineJoin}
        propertyType="select"
        selectOptions={[
          { value: 'bevel', label: 'Bevel' },
          { value: 'round', label: 'Round' },
          { value: 'miter', label: 'Miter' },
        ]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* Line Miter Limit */}
      <StylePropertyInput
        key={`${layer.id}-line-miter-limit`}
        layerId={layer.id}
        propertyName="lineMiterLimit"
        label="Miter Limit"
        currentValue={layer.lineMiterLimit}
        propertyType="number"
        numberConfig={{ min: 0 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* Line Round Limit */}
      <StylePropertyInput
        key={`${layer.id}-line-round-limit`}
        layerId={layer.id}
        propertyName="lineRoundLimit"
        label="Round Limit"
        currentValue={layer.lineRoundLimit}
        propertyType="number"
        numberConfig={{ min: 0 }}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* Line Sort Key */}
      <StylePropertyInput
        key={`${layer.id}-line-sort-key`}
        layerId={layer.id}
        propertyName="lineSortKey"
        label="Sort Key"
        currentValue={layer.lineSortKey}
        propertyType="number"
        numberConfig={{}}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
      {/* Visibility */}
      <StylePropertyInput
        key={`${layer.id}-visibility`}
        layerId={layer.id}
        propertyName="visibility"
        label="Visibility"
        currentValue={layer.visibility}
        propertyType="select"
        selectOptions={[
          { value: 'visible', label: 'Visible' },
          { value: 'none', label: 'None' },
        ]}
        availablePropertyKeys={layer.availablePropertyKeys}
        onStyleChange={updateLayerStyle}
        isExpressionPossible={true}
        disabled={isDisabled}
      />
    </>
  );
};

export const LinePaintProperties: React.FC<StyleEditorSectionProps> = ({ layer, updateLayerStyle, isDisabled }) => {
  if (layer.maplibreLayerType !== 'line') return null;
  const uniqueIdPrefix = `line-paint-${layer.id}`;

  return (
    <HierarchicalAccordion style={{ width: '100%' }} defaultOpenId={`${uniqueIdPrefix}-base-appearance`}>
      <HierarchicalAccordionItem title="Aspetto Base" level={2} id={`${uniqueIdPrefix}-base-appearance`}>
        <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
          {/* Line Color */}
          <StylePropertyInput
            key={`${layer.id}-line-color`}
            layerId={layer.id}
            propertyName="lineColor"
            label="Line Color"
            currentValue={layer.lineColor}
            propertyType="color"
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Opacity */}
          <StylePropertyInput
            key={`${layer.id}-line-opacity`}
            layerId={layer.id}
            propertyName="lineOpacity"
            label="Line Opacity"
            currentValue={layer.lineOpacity}
            propertyType="range"
            numberConfig={{ min: 0, max: 1, step: 0.01 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
            hideNumericValueDisplay={true}
          />
          {/* Line Width */}
          <StylePropertyInput
            key={`${layer.id}-line-width`}
            layerId={layer.id}
            propertyName="lineWidth"
            label="Line Width"
            currentValue={layer.lineWidth}
            propertyType="number"
            numberConfig={{ min: 0, step: 0.1 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Blur */}
          <StylePropertyInput
            key={`${layer.id}-line-blur`}
            layerId={layer.id}
            propertyName="lineBlur"
            label="Blur"
            currentValue={layer.lineBlur}
            propertyType="number"
            numberConfig={{ min: 0 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
        </div>
      </HierarchicalAccordionItem>

      <HierarchicalAccordionItem title="Tratteggio/Pattern/Gradiente" level={2} id={`${uniqueIdPrefix}-dash-pattern-gradient`}>
        <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
          {/* Line Dasharray */}
          <StylePropertyInput
            key={`${layer.id}-line-dasharray`}
            layerId={layer.id}
            propertyName="lineDasharray"
            label="Dash Array"
            currentValue={layer.lineDasharray}
            propertyType="array"
            numberConfig={{ min:0 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Pattern */}
          <StylePropertyInput
            key={`${layer.id}-line-pattern`}
            layerId={layer.id}
            propertyName="linePattern"
            label="Pattern (Image ID)"
            currentValue={layer.linePattern}
            propertyType="text"
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Gradient */}
          <StylePropertyInput
            key={`${layer.id}-line-gradient`}
            layerId={layer.id}
            propertyName="lineGradient"
            label="Line Gradient"
            currentValue={layer.lineGradient}
            propertyType="color"
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
        </div>
      </HierarchicalAccordionItem>

      <HierarchicalAccordionItem title="Posizionamento e Offset" level={2} id={`${uniqueIdPrefix}-positioning-offset`}>
        <div className="accordion-content" style={{ padding: 'var(--spacing-xs) var(--spacing-sm)'}}>
          {/* Line Gap Width */}
          <StylePropertyInput
            key={`${layer.id}-line-gap-width`}
            layerId={layer.id}
            propertyName="lineGapWidth"
            label="Gap Width"
            currentValue={layer.lineGapWidth}
            propertyType="number"
            numberConfig={{ min: 0 }}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Offset */}
          <StylePropertyInput
            key={`${layer.id}-line-offset`}
            layerId={layer.id}
            propertyName="lineOffset"
            label="Offset"
            currentValue={layer.lineOffset}
            propertyType="number"
            numberConfig={{}}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Translate */}
          <StylePropertyInput
            key={`${layer.id}-line-translate`}
            layerId={layer.id}
            propertyName="lineTranslate"
            label="Translate (X, Y)"
            currentValue={layer.lineTranslate}
            propertyType="array"
            numberConfig={{}}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
          {/* Line Translate Anchor */}
          <StylePropertyInput
            key={`${layer.id}-line-translate-anchor`}
            layerId={layer.id}
            propertyName="lineTranslateAnchor"
            label="Translate Anchor"
            currentValue={layer.lineTranslateAnchor}
            propertyType="select"
            selectOptions={[
              { value: 'map', label: 'Map' },
              { value: 'viewport', label: 'Viewport' },
            ]}
            availablePropertyKeys={layer.availablePropertyKeys}
            onStyleChange={updateLayerStyle}
            isExpressionPossible={true}
            disabled={isDisabled}
          />
        </div>
      </HierarchicalAccordionItem>
    </HierarchicalAccordion>
  );
};

const LineStyleEditor: React.FC<StyleEditorSectionProps> = () => {
  // Questo componente non renderizza più direttamente l'intera UI.
  return null;
};

export default LineStyleEditor;